import { useMemo } from 'react';
import BigNumber from 'bignumber.js';
import { useQuery } from '@ywwwtseng/react-kit';
import * as web3 from '@ywwwtseng/web3';
import * as schema from '@/db/schema';
import type { SelectToken } from '@/types';
import { displayAmount, getBalancePrice } from '@/utils/token';

import { useTokenPrice } from './useTokenPrice';

export function useUserBalance(token?: SelectToken) {
  const { data: balances } = useQuery<
    (typeof schema.user_balances.$inferSelect)[]
  >('user_balances', {
    refetchOnMount: true,
  });

  const { value: tokenPrice } = useTokenPrice(token?.id);

  const rawBalance = useMemo(() => {
    if (!token) return null;
    return balances?.find((b) => b.token_id === token.id) ?? null;
  }, [balances, token]);

  /** 總餘額（amount） */
  const totalValue = useMemo(() => {
    if (!token || !rawBalance) return '0';
    return rawBalance.amount ?? '0';
  }, [rawBalance, token]);

  /** 鎖定數量（locked_amount），發禮/待處理等 */
  const lockedValue = useMemo(() => {
    if (!token || !rawBalance) return '0';
    return rawBalance.locked_amount ?? '0';
  }, [rawBalance, token]);

  /** 可用餘額 = amount - locked_amount，提領與發禮以此為準 */
  const value = useMemo(() => {
    const total = new BigNumber(totalValue);
    const locked = new BigNumber(lockedValue);
    return total.minus(locked).gte(0) ? total.minus(locked).toFixed() : '0';
  }, [totalValue, lockedValue]);

  const formatted = useMemo(() => {
    if (!token) return '0';
    return web3.utils.formatUnits(value, token.decimals);
  }, [value, token]);

  const totalFormatted = useMemo(() => {
    if (!token) return '0';
    return web3.utils.formatUnits(totalValue, token.decimals);
  }, [totalValue, token]);

  const lockedFormatted = useMemo(() => {
    if (!token) return '0';
    return web3.utils.formatUnits(lockedValue, token.decimals);
  }, [lockedValue, token]);

  const price = useMemo(() => {
    if (!tokenPrice || !value || !token) return undefined;
    return displayAmount(
      getBalancePrice({
        token: { price: tokenPrice, decimals: token.decimals },
        amount: value,
      }),
    );
  }, [value, token, tokenPrice]);

  const hasLocked = useMemo(
    () => new BigNumber(lockedValue).gt(0),
    [lockedValue]
  );

  return {
    value,
    formatted,
    price,
    totalValue,
    totalFormatted,
    lockedValue,
    lockedFormatted,
    hasLocked,
  };
}
